// src/utils/SafeAreaProvider.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Reads real safe-area insets and publishes them as CSS custom properties.
// Also initialises the NavigationBarManager (status bar + nav bar colors).
//
// CSS variables published:
//   --safe-area-inset-top      (px)  ← status bar height
//   --safe-area-inset-bottom   (px)  ← nav bar / gesture bar height
//   --safe-area-inset-left     (px)
//   --safe-area-inset-right    (px)
//   --safe-area-top            (px)  ← alias (backward compat)
//   --safe-area-bottom         (px)  ← alias (backward compat)
//   --header-height            (px)  ← toolbar height (56dp / 64dp tablet)
//   --header-height-with-safe-area   ← header-height + inset-top
//   --footer-height-with-safe-area   ← footer height + inset-bottom
// ─────────────────────────────────────────────────────────────────────────────

import { createContext, useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { NavigationBarManager } from './NavigationBarManager';

const SafeAreaContext = createContext({
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

export const useSafeArea = () => useContext(SafeAreaContext);

async function safeCallAsync(fn, context) {
  try {
    return await fn();
  } catch (err) {
    console.warn(`[SafeAreaProvider] ${context}:`, err?.message || err);
    return undefined;
  }
}

/**
 * Reads the real safe-area-inset-top from the browser's CSS environment.
 *
 * Strategy:
 *  1. Create a 0×0 fixed element at top:env(safe-area-inset-top, 0px)
 *  2. Wait one rAF so the browser resolves the env() value
 *  3. Read back getComputedStyle().top
 *
 * This works correctly on:
 *  - Android < 15 with overlaysWebView:false  → returns 0 (bar is native, not inset)
 *  - Android 15+ (opt-out of edge-to-edge)    → returns status bar height reported by OS
 *  - Web / non-native                         → returns 0
 */
async function readSafeAreaTop() {
  return new Promise((resolve) => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:env(safe-area-inset-top,0px);left:0;width:0;height:0;' +
      'pointer-events:none;opacity:0;z-index:-1;';
    document.body.appendChild(probe);

    requestAnimationFrame(() => {
      const value = parseFloat(getComputedStyle(probe).top) || 0;
      document.body.removeChild(probe);
      resolve(value);
    });
  });
}

async function readSafeAreaBottom() {
  return new Promise((resolve) => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;bottom:env(safe-area-inset-bottom,0px);left:0;width:0;height:0;' +
      'pointer-events:none;opacity:0;z-index:-1;';
    document.body.appendChild(probe);

    requestAnimationFrame(() => {
      const rect = probe.getBoundingClientRect();
      const value = Math.max(0, window.innerHeight - rect.bottom);
      document.body.removeChild(probe);
      resolve(value);
    });
  });
}

/**
 * Detect tablet/large screen to use 64dp toolbar instead of 56dp.
 * Google Material spec: tablets ≥ 600dp use 64dp app bar height.
 */
function getToolbarHeight() {
  // window.screen.width / devicePixelRatio gives logical width in dp
  const dpWidth = window.screen.width / window.devicePixelRatio;
  return dpWidth >= 600 ? 64 : 56;
}

export const SafeAreaProvider = ({ children }) => {
  const [insets, setInsets] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

  useEffect(() => {
    setupSafeArea();
  }, []);

  const setupSafeArea = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await safeCallAsync(
          () => NavigationBarManager.initialize(),
          'NavigationBarManager.initialize'
        );
      }

      // Read real safe-area insets from CSS env()
      let topInset    = await readSafeAreaTop();
      let bottomInset = await readSafeAreaBottom();

      const platform = Capacitor.getPlatform();

      const toolbarHeight = getToolbarHeight();
      const footerHeight = 60;

      const root = document.documentElement;

      // Primary safe-area custom properties
      root.style.setProperty('--safe-area-inset-top', `${topInset}px`);
      root.style.setProperty('--safe-area-inset-bottom', `${bottomInset}px`);
      root.style.setProperty('--safe-area-inset-left', '0px');
      root.style.setProperty('--safe-area-inset-right', '0px');

      // Backward-compatible aliases
      root.style.setProperty('--safe-area-top', `${topInset}px`);
      root.style.setProperty('--safe-area-bottom', `${bottomInset}px`);
      root.style.setProperty('--safe-area-left', '0px');
      root.style.setProperty('--safe-area-right', '0px');

      root.style.setProperty('--toolbar-height', `${toolbarHeight}px`);
      root.style.setProperty('--header-height',  `${toolbarHeight}px`);
      root.style.setProperty('--footer-height',  `${footerHeight}px`);

      setInsets({ top: topInset, bottom: bottomInset, left: 0, right: 0 });

      console.log('[SafeAreaProvider]', {
        topInset,
        bottomInset,
        toolbarHeight,
        platform,
      });
    } catch (error) {
      console.error('[SafeAreaProvider] setup failed:', error);
    }
  };

  return (
    <SafeAreaContext.Provider value={insets}>
      {children}
    </SafeAreaContext.Provider>
  );
};
